import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { wrapComponent } from 'utils/framework';
import {
    Box, Button, Icon, Flex, Link
} from 'components/ui';
import Empty from 'constants/empty';
import Modal from 'components/Modal/Modal';
import FilterChiclets from 'components/Catalog/Filters/FilterChiclets';
import FilterItem from 'components/Catalog/Filters/FilterItem/FilterItem';
import Pill from 'components/Pill';
import {
    space, colors, fontSizes, mediaQueries
} from 'style/config';

function MyListsCategoryFilter(props) {
    const {
        selectedCategories: selectedCategoriesProp,
        setCategoriesFilter,
        availableCategories,
        categoryText,
        clearAllText,
        showResultsText
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState(selectedCategoriesProp);
    const [totalItemsFiltered, setTotalItemsFiltered] = useState(null);
    useEffect(() => {
        setSelectedCategories(selectedCategoriesProp);
        const updatedTotal = calculateTotal();

        setTotalItemsFiltered(updatedTotal);
    }, [selectedCategoriesProp, isOpen]);

    const calculateTotal = () => {
        let updatedTotal = 0;

        availableCategories.forEach(category => {
            const categoryExists = selectedCategories.includes(category.refinementValue);

            if (categoryExists) {
                updatedTotal += category.count;
            }
        });

        return updatedTotal;
    };

    const requestClose = applyChanges => {
        setIsOpen(false);

        setSelectedCategories(prev => {
            const finalValue = applyChanges ? prev : selectedCategoriesProp;

            if (applyChanges) {
                setCategoriesFilter(finalValue);
            }

            return finalValue;
        });
    };

    const handleSelectCategory = (category, count) => {
        setSelectedCategories(prev => {
            const categoryExists = prev.includes(category);

            const updatedCategories = categoryExists ? prev.filter(cat => cat !== category) : [...prev, category];

            const updatedTotal = categoryExists ? totalItemsFiltered - count : totalItemsFiltered + count;

            setTotalItemsFiltered(updatedTotal);

            return updatedCategories;
        });
    };

    const handleClearAll = () => {
        setSelectedCategories([]);
        setTotalItemsFiltered(null);
    };

    const handleSubmit = () => {
        requestClose(true);
    };

    const openModal = () => {
        setIsOpen(true);
    };

    const handleRemoveChiclet = (_filterName, optionValue) => {
        setSelectedCategories(prev => {
            const updatedCategories = prev.filter(cat => cat !== optionValue);
            const categoryToRemove = availableCategories.find(cat => cat.refinementValue === optionValue);
            setTotalItemsFiltered(totalItemsFiltered - categoryToRemove.count);

            return updatedCategories;
        });
    };

    const getFilterItem = category => {
        const isSelected = selectedCategories.includes(category.refinementValue);

        return (
            <FilterItem
                key={category.key}
                type='checkboxes'
                title={category.refinementValueDisplayName}
                value={category}
                selected={isSelected}
                onClick={() => handleSelectCategory(category.refinementValue, category.count)}
            />
        );
    };

    return (
        <Box>
            <Pill
                fontSize={[fontSizes.sm, fontSizes.base]}
                onClick={openModal}
                isActive={selectedCategoriesProp.length > 0}
                css={styles.pillSort}
            >
                {categoryText}
                <Icon
                    className='Accordion-icon'
                    name='caretDown'
                    size={15}
                    marginLeft={1}
                />
            </Pill>

            <Modal
                isOpen={isOpen}
                onDismiss={requestClose}
                width={0}
                hasBodyScroll
                isDrawer
            >
                <Modal.Header>
                    <Modal.Title>{categoryText}</Modal.Title>
                </Modal.Header>

                <Modal.Body
                    lineHeight='tight'
                    paddingBottom={2}
                    paddingTop={2}
                >
                    {availableCategories.map(category => getFilterItem(category))}
                </Modal.Body>

                <Modal.Footer
                    paddingX={[4, 4]}
                    css={selectedCategories.length > 0 && { ...styles.tightPadding }}
                >
                    {selectedCategories.length > 0 && (
                        <Box css={styles.filterChicletsContainer}>
                            <FilterChiclets
                                refinements={[
                                    {
                                        displayName: 'Category',
                                        type: 'checkboxes',
                                        values: availableCategories
                                    }
                                ]}
                                filters={{ Category: selectedCategories }}
                                onRemoveChiclet={handleRemoveChiclet}
                                isModal
                                customCss={styles.customCss}
                            />
                        </Box>
                    )}

                    <Flex css={styles.ctaContainer}>
                        <Link
                            css={styles.clearAllLink}
                            children={clearAllText}
                            onClick={handleClearAll}
                        />

                        <Button
                            css={styles.submitButton}
                            onClick={handleSubmit}
                            variant='primary'
                        >
                            {`${showResultsText} ${totalItemsFiltered ? `(${totalItemsFiltered})` : ''}`}
                        </Button>
                    </Flex>
                </Modal.Footer>
            </Modal>
        </Box>
    );
}

const styles = {
    ctaContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        marginTop: 2
    },
    clearAllLink: {
        color: colors['blue'],
        padding: space[0]
    },
    tightPadding: {
        paddingTop: `${space[2]}px !important`
    },
    customCss: {
        divider: {
            marginX: [-space[3], -space[4]]
        },
        chicletWrapper: {
            [mediaQueries.sm]: {
                marginLeft: `-${space[4]}px`,
                marginRight: `-${space[4]}px`,
                paddingLeft: `${space[4]}px`,
                paddingRight: `${space[4]}px`
            }
        }
    },
    submitButton: {
        width: '50%'
    },
    pillSort: {
        marginLeft: space[2],
        maxHeight: 36,
        fontSize: fontSizes.base,
        [mediaQueries.xsMax]: {
            fontSize: fontSizes.sm
        }
    }
};

MyListsCategoryFilter.defaultProps = {
    categoryText: Empty.String,
    clearAllText: Empty.String,
    showResultsText: Empty.String,
    setCategoriesFilter: Empty.Function,
    selectedCategories: Empty.Array,
    availableCategories: Empty.Array
};

MyListsCategoryFilter.propTypes = {
    categoryText: Empty.String,
    clearAllText: PropTypes.string,
    showResultsText: PropTypes.string,
    setCategoriesFilter: PropTypes.func,
    selectedCategories: PropTypes.array,
    availableCategories: PropTypes.array
};

export default wrapComponent(MyListsCategoryFilter, 'MyListsCategoryFilter');
